import { html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_EASM_DNS_RECORDS } from '../../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { EasmDnsRecordsPayload } from '@nirvana/shared/dtos/types/widgets/common';
import bb from 'billboard.js/dist/billboard.pkgd';
import { ChartClickInfo, EasmDnsRecordsChartType, EasmDnsRecordsEvents, EasmDnsRecordsWidgetSize } from '../types';
import { formatLargeNumber } from '../../../../utils/formatter.utils';
import { store } from '../../../../redux/store';
import { selectDashboardFilterValues } from '../../../../genericDashboard/redux/dashboard.filters.slice';
import { GenericDashboardsFiltersHelper } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { generateFiltersForInventory } from '../../../utils/utils';
import { FilterType } from '../../genericTopData/types/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { FindingType } from '../../../types';

/**
 * @summary This is a Web Component widget used in the EASM Dashboard. It is based on the BarChart implementation of the billboard.js library
 * https://naver.github.io/billboard.js/demo/#Chart.BarChart
 * @tag gz-easm-dns-records
 * @fires easm-dns-records-data-click - A custom event dispatched when a data region is clicked (a vertical bar and its padding)
 */
@customElement(WEB_COMPONENT_EASM_DNS_RECORDS)
export class GzEasmDnsRecordsWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true, reflect: true })
  payload: EasmDnsRecordsPayload;

  @property({ type: Array, attribute: true, reflect: true })
  resizeBreakingPoints: number[] = [];

  @query('#chart-container')
  chartContainer: HTMLElement;

  private readonly chartLeftPadding = 40;
  private readonly chartRightPadding = 10;

  static get styles() {
    return styles;
  }

  async updated(): Promise<void> {
    await this.updateComplete;
    this.buildChart();
  }

  private computeChartClickInfo(event: MouseEvent): ChartClickInfo {
    const chartSvgRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const svgEventX = event.clientX - chartSvgRect.x;
    const svgEventY = event.clientY - chartSvgRect.y;
    return { chartSvgRect, svgEventX, svgEventY };
  }

  private isClickOutsideChart(chartClickInfo: ChartClickInfo): boolean {
    const { chartSvgRect, svgEventX, svgEventY } = chartClickInfo;
    return (
      svgEventX < this.chartLeftPadding ||
      svgEventX >= chartSvgRect.width - this.chartRightPadding ||
      svgEventY > chartSvgRect.height - 50 // The bottom space of the chart (occupied by the Y axis) is 50px
    );
  }

  private isClickedOnLegend(chartClickInfo: ChartClickInfo): boolean {
    const { chartSvgRect, svgEventX, svgEventY } = chartClickInfo;
    return (
      svgEventX > this.chartLeftPadding &&
      svgEventX <= chartSvgRect.width - this.chartRightPadding &&
      svgEventY > chartSvgRect.height - 50 && // The bottom space of the chart (occupied by the Y axis) is 50px
      svgEventY <= chartSvgRect.height
    );
  }

  private computeClickedRecordIndex(chartClickInfo: ChartClickInfo): number {
    const { chartSvgRect, svgEventX } = chartClickInfo;
    const chartWidth = chartSvgRect.width - this.chartLeftPadding - this.chartRightPadding;
    const chartElementsCounter = this.payload?.dnsRecords.records.length;
    const eventPosition = svgEventX - this.chartLeftPadding;
    return Math.floor((chartElementsCounter * eventPosition) / chartWidth);
  }

  private onChartClick(event: MouseEvent): void {
    // based on the position of the click event, compute which item region has been clicked
    if (!event.currentTarget || !this.payload) {
      return;
    }

    const chartClickInfo = this.computeChartClickInfo(event);
    if (this.isClickedOnLegend(chartClickInfo)) {
      this.handleLegendClick(event);
      return;
    }

    if (this.isClickOutsideChart(chartClickInfo)) {
      return;
    }

    const clickedRecordIndex = this.computeClickedRecordIndex(chartClickInfo);
    const clickedRecord = this.payload?.dnsRecords.records[clickedRecordIndex];

    this.dispatchEvent(
      new CustomEvent(EasmDnsRecordsEvents.DATA_CLICK, {
        detail: { record: clickedRecord, index: clickedRecordIndex },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleLegendClick(event: MouseEvent): void {
    const legendOx = this.chartContainer.querySelector('.bb-axis-x');
    if (legendOx) {
      const legendItems = Array.from(legendOx.querySelectorAll('tspan'));
      legendItems.forEach((legendItem, index) => {
        const legendItemRect = legendItem.getBoundingClientRect();
        const errorOffset = 1;
        if (
          event.x >= legendItemRect.left - errorOffset &&
          event.x <= legendItemRect.right + errorOffset &&
          event.y >= legendItemRect.top - errorOffset &&
          event.y <= legendItemRect.bottom + errorOffset
        ) {
          const clickedRecord = this.payload?.dnsRecords.records[index];
          this.dispatchEvent(
            new CustomEvent(EasmDnsRecordsEvents.LEGEND_CLICK, {
              detail: { record: clickedRecord, index: index },
              bubbles: true,
              composed: true,
            })
          );
        }
      });
    }
  }

  private formatYValues(value: number): string {
    if (this.state === WidgetStateFrontEnd.IS_EMPTY) {
      return '- -';
    }
    return formatLargeNumber(value);
  }

  private getTooltipPosition(_, __, ___, element: SVGElement, pos: { xAxis: number }): { top: number; left: number } {
    // docs: https://naver.github.io/billboard.js/release/3.8.2/doc/Options.html#.tooltip
    const top = element.getBoundingClientRect().height / 2; // element is the events rectangle placed over the chart
    const left = pos.xAxis + this.chartLeftPadding; // this is the horizontal middle of a vertical bar
    return { top, left }; // {top, left} is the middle of the vertical bar visual space; we translate the tooltip with css
  }

  private renderTooltip([{ value }]): string {
    return `<div class="chart-tooltip">${value}</div>`;
  }

  protected computeChartLabelsFontSize(): void {
    if (!this.hasResizeLimits()) {
      if (this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
        this.chartContainer.setAttribute('size', EasmDnsRecordsWidgetSize.SMALL);
        return;
      }
    }
    this.chartContainer.setAttribute('size', EasmDnsRecordsWidgetSize.MEDIUM);
  }

  private buildChart(): void {
    if (!this.payload || !this.chartContainer || !this.payload.dnsRecords || !this.payload.dnsRecords.records) {
      return;
    }
    // If state is empty, put as default value 8 s.t. the horizontal lines of the chart align with the vertical bars.
    // Otherwise, put the record value on the chart
    const values = this.payload?.dnsRecords.records.map(record =>
      this.state === WidgetStateFrontEnd.IS_EMPTY ? 8 : record.value
    );
    const labels = this.payload?.dnsRecords.records.map(record => record.label);
    const chartType =
      this.state === WidgetStateFrontEnd.IS_EMPTY ? EasmDnsRecordsChartType.EMPTY : EasmDnsRecordsChartType.ACTIVE;

    this.computeChartLabelsFontSize();
    this.generateChart(chartType, values, labels);
  }

  private generateChart(chartType: EasmDnsRecordsChartType, values: number[], labels: string[]): any {
    // chart options configuration docs: https://naver.github.io/billboard.js/release/3.8.2/doc/Options.html
    return bb.generate({
      data: {
        columns: [[chartType, ...values]],
        colors: {
          [EasmDnsRecordsChartType.ACTIVE]: 'var(--blue-layout-300-active)',
          [EasmDnsRecordsChartType.EMPTY]: 'var(--chart-101-pressed-dividers)',
        },
        type: 'bar',
      },
      onclick: this.onChartClick.bind(this),
      bar: {
        width: { ratio: 0.5 },
      },
      grid: {
        y: {
          show: true,
          ticks: 5,
        },
      },
      axis: {
        x: {
          type: 'category',
          categories: labels,
          tick: {
            width: 100,
            outer: false,
          },
        },
        y: {
          tick: {
            format: this.formatYValues.bind(this),
            culling: { max: 5 },
          },
        },
      },
      tooltip: {
        position: this.getTooltipPosition.bind(this),
        contents: this.renderTooltip.bind(this),
      },
      padding: {
        left: this.chartLeftPadding,
        right: this.chartRightPadding,
      },
      legend: { hide: true },
      interaction: { enabled: this.state !== WidgetStateFrontEnd.IS_EMPTY },
      bindto: this.chartContainer,
    });
  }

  protected handleResize(): void {
    super.handleResize();
    this.buildChart();
  }

  protected renderContent(): TemplateResult {
    return html` <div id="chart-container"></div> `;
  }

  firstUpdated(): void {
    super.firstUpdated();

    this.addEventListener(EasmDnsRecordsEvents.DATA_CLICK, async (event: CustomEvent) => {
      await this.navigateToInventory(event);
    });

    this.addEventListener(EasmDnsRecordsEvents.LEGEND_CLICK, async (event: CustomEvent) => {
      await this.navigateToInventory(event);
    });
  }

  private async navigateToInventory(event: CustomEvent): Promise<void> {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const recordTypeFilters = generateFiltersForInventory(FilterType.RECORD_TYPE, '', companyId, [
      event.detail.record.label,
    ]);
    const assetTypeFilters = generateFiltersForInventory(FilterType.FINDING_TYPE, '', companyId, [
      FindingType.DNS_RECORD,
    ]);

    await NavigationHelper.navigateToEASMArtifactsInventory({
      smartViewId: this.payload?.inventorySmartViewId as string,
      filters: [...recordTypeFilters, ...assetTypeFilters],
    });
  }

  public async handleTitleClick() {
    const dashboardAppState = store.getState().dashboardsApp;
    const filters = selectDashboardFilterValues(dashboardAppState);
    const companyId = GenericDashboardsFiltersHelper.getCompanyIdFromSingleSelectCompanyFilter(filters) as string;

    const filtersToSend = generateFiltersForInventory(FilterType.NONE, '', companyId, []);
    await NavigationHelper.navigateToEASMArtifactsInventory({
      smartViewId: this.payload?.inventorySmartViewId as string,
      filters: filtersToSend,
    });
  }
}
